// src/pages/healthcare/consultation/consultationComponents/ConsultDrawerTitle.tsx
import React, { ReactNode } from "react";
import { Badge, Button, Space, Tooltip, Typography } from "antd";
import { CommentOutlined, PlayCircleOutlined, SettingOutlined, StopOutlined, UserOutlined } from "@ant-design/icons";

import { Patient } from "../../../../types/healthcare/PatientTypes";
import { Appointment } from "../../../../types/appointments/AppointmentTypes";
import { CAPTURE_STATE_LABELS, CaptureState } from "../../../../hooks/consult/useConsultTranscripts";

const { Title, Text } = Typography;

interface ConsultDrawerTitleProps {
	patient: Patient | null;
	appointment: Appointment;
	captureState: CaptureState | null;
	reconnecting?: boolean;
	consultationStarted: boolean;
	onStart: () => void;
	onStop: () => void;
	transcriptCount: number;
	onOpenTranscript: () => void;
	onOpenPatientProfile: () => void;
	onOpenSettings: () => void;
	readOnly: boolean;
	/** Page-specific toolbar buttons, rendered between Profile and Settings. */
	actions?: ReactNode;
}

const ConsultDrawerTitle: React.FC<ConsultDrawerTitleProps> = ({
	patient,
	appointment,
	captureState,
	reconnecting,
	consultationStarted,
	onStart,
	onStop,
	transcriptCount,
	onOpenTranscript,
	onOpenPatientProfile,
	onOpenSettings,
	readOnly,
	actions,
}) => {
	return (
		<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
			<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
				<Title level={5} style={{ margin: 0, fontSize: 14 }}>
					{patient?.first_name} {patient?.last_name}
				</Title>
				<Text type="secondary" style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>
					{appointment.start_time ? new Date(appointment.start_time).toLocaleDateString() : ""}
				</Text>
				{reconnecting && <Badge status="processing" text={<Text style={{ fontSize: 11, color: "rgba(255,255,255,0.9)" }}>Reconnecting...</Text>} />}
				{captureState && (
					<Badge
						status={captureState === "recording" ? "success" : captureState === "off" ? "default" : "error"}
						text={<Text style={{ fontSize: 11, color: "rgba(255,255,255,0.9)" }}>{CAPTURE_STATE_LABELS[captureState]}</Text>}
						data-testid="consultation-capture-indicator"
						data-capture-state={captureState}
					/>
				)}
			</div>
			<Space size={4}>
				<Tooltip title="Transcript">
					<Badge count={transcriptCount} size="small" offset={[-2, 2]}>
						<Button type="text" size="small" icon={<CommentOutlined />} onClick={onOpenTranscript} data-testid="consultation-transcript-btn" />
					</Badge>
				</Tooltip>
				<Tooltip title="Patient Profile">
					<Button type="text" size="small" icon={<UserOutlined />} onClick={onOpenPatientProfile} />
				</Tooltip>
				{!readOnly && actions}
				{!readOnly && (
					<>
						<Tooltip title="Settings">
							<Button type="text" size="small" icon={<SettingOutlined />} onClick={onOpenSettings} data-testid="consultation-settings-btn" />
						</Tooltip>
						<Tooltip title={consultationStarted ? "Stop Consultation" : "Start"}>
							<Button
								type={consultationStarted ? "primary" : "default"}
								size="small"
								icon={consultationStarted ? <StopOutlined /> : <PlayCircleOutlined />}
								onClick={consultationStarted ? onStop : onStart}
								danger={consultationStarted}
								data-testid="consultation-start-btn"
							/>
						</Tooltip>
					</>
				)}
			</Space>
		</div>
	);
};

export default ConsultDrawerTitle;
